// Three stat tiles, not a chart: the job is a glance answer ("are reports piling
// up unfinished?"), and a plot would be decoration. A 7-day trend sparkline is
// deliberately deferred.
//
// All three numbers come from ONE cohort — the distinct jobs first opened in the
// last 7 days — so they add up. See HomeServices::InspectionReportActivityMetrics
// for why mixing populations would make the rate meaningless.

import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useGet } from '../../../sharedModules/hooks/useQuery';

interface ActivityMetrics {
  completed: number;
  abandoned: number;
  completion_rate: number | null;
  cohort_days: number;
}

const TILE_LABEL_SX = {
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontSize: '0.7rem',
  opacity: 0.7,
} as const;

const MetricTile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Grid item xs={12} sm={4}>
    <Card sx={{ boxShadow: 1 }}>
      <CardContent>
        <Typography sx={TILE_LABEL_SX}>{label}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
);

const InspectionReportMetricStrip: React.FC = () => {
  const { data, isLoading } = useGet<ActivityMetrics>({
    url: '/home_services/inspection_report_activities/metrics',
  });

  const metrics = data?.data;
  const days = metrics?.cohort_days ?? 7;

  // Em dash while loading and for an empty cohort — never "0%" for "no data",
  // and never a division by zero.
  const format = (value: number | null | undefined): string => (isLoading || value == null ? '—' : String(value));
  const rate = metrics?.completion_rate;

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <MetricTile label={`Opened & completed · ${days}d`} value={format(metrics?.completed)} />
      <MetricTile label={`Opened & not completed · ${days}d`} value={format(metrics?.abandoned)} />
      <MetricTile label="Completion rate" value={isLoading || rate == null ? '—' : `${Math.round(rate * 100)}%`} />
    </Grid>
  );
};

export default InspectionReportMetricStrip;
